<template>
  <div class="auth-form auth-form--login w-100">
    <h1 class="auth-form__title text-h5 font-weight-semibold mb-4 mb-lg-8">
      Iniciar sesión
    </h1>

    <v-form ref="authForm" @submit.prevent="handleSubmit">
      <div class="d-flex flex-column gap-y-4">
        <TipoDocumentoTextField
          v-model:tipo-id="documentTipoId"
          v-model:numero="documentNumber"
          native-autocomplete="username"
          native-name="username"
          force-floating-label
          :rules-nro-doc="[rules.required]"
        />
        <v-text-field
          ref="passwordField"
          v-model="password"
          label="Contraseña"
          placeholder=" "
          :type="showPassword ? 'text' : 'password'"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :rules="[rules.required]"
          name="password"
          autocomplete="current-password"
          active
          dirty
          :append-inner-icon="showPassword ? eyeOffIcon : eyeIcon"
          @click:append-inner="showPassword = !showPassword"
        />


        <v-btn
          type="submit"
          block
          size="large"
          class="mt-2"
          color="primary"
          :loading="isLoading"
          density="comfortable"
        >
          {{ isLoading ? 'Ingresando...' : 'INGRESAR' }}
        </v-btn>
      </div>
    </v-form>

    <div class="text-center mt-4">
      <v-btn variant="text" color="primary" class="text-body-2 text-medium-emphasis" @click="$emit('forgot-password')">
        ¿Olvidó su contraseña?
      </v-btn>
    </div>

    <v-divider class="my-4 my-lg-8" />

    <p class="text-center text-body-2 font-weight-medium text-medium-emphasis mb-4">
      ¿NO TIENE UNA CUENTA?
    </p>

    <div v-if="mostrarRegistroPortal" class="d-flex align-center justify-center">
      <v-btn
        variant="text"
        color="primary"
        class="text-body-2 font-weight-medium mb-2 align-center justify-center"
        @click="goToRegister"
      >
        Cree una cuenta
      </v-btn>
    </div>
    <div v-if="mostrarAutogestionTurno" class="d-flex align-center justify-center">
      <v-btn
        variant="text"
        color="primary"
        class="text-body-2 font-weight-medium mb-2 align-center justify-center"
        @click="$emit('autogestionar-turno')"
      >
        Saque un turno rápido
      </v-btn>
    </div>

    <div v-if="mobileHelpHtml" class="mobile-help d-lg-none mt-6">
      <v-divider class="mb-5" />
      <v-expansion-panels variant="accordion" class="mobile-help__panels">
        <v-expansion-panel elevation="0" class="mobile-help__panel">
          <v-expansion-panel-title class="mobile-help__title">
            <div class="d-flex align-center min-width-0">
              <v-icon :icon="helpIcon" size="18" class="mobile-help__icon mr-2" />
              <span class="text-body-2 font-weight-semibold text-truncate">
                ¿Necesita ayuda para ingresar?
              </span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="mobile-help__text">
            <div class="mobile-help-html text-body-2" v-html="mobileHelpHtml" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import enums from '../../utils/enums'
import rules from '../../utils/helpers/rules'
import TipoDocumentoTextField from '../shared/TipoDocumentoTextField.vue'

export default {
  name: 'LoginAuthForm',
  components: { TipoDocumentoTextField },
  props: {
    hospitalConfig: {
      type: Object,
      required: true,
    },
  },
  emits: ['forgot-password', 'register', 'autogestionar-turno'],
  computed: {
    mostrarRegistroPortal() {
      return this.hospitalConfig?.portalPacienteImplementado === true
    },
    mostrarAutogestionTurno() {
      return this.hospitalConfig?.autogestionImplementada === true
    },
    mobileHelpHtml() {
      return String(this.hospitalConfig?.helpTextLogin ?? '').trim()
    },
  },
  data() {
    return {
      eyeIcon: enums.icons.EYE,
      eyeOffIcon: enums.icons.EYE_OFF,
      helpIcon: enums.icons.HELP_OUTLINE,
      documentTipoId: null,
      documentNumber: '',
      password: '',
      showPassword: false,
      isLoading: false,
      rules,
      pwAutofillTimeouts: [],
      pwAutofillOnAnim: null,
    }
  },
  mounted() {
    this.$nextTick(() => this.setupPasswordAutofillSync())
  },
  beforeUnmount() {
    this.teardownPasswordAutofillSync()
  },
  methods: {
    ...mapActions({
      setAlert: 'alerts/setAlert',
      loginWithDocument: 'auth/loginWithDocument',
    }),
    ...mapMutations({
      resetGuestSession: 'guestSession/resetGuestSession',
    }),
    getPasswordNativeInput() {
      const root = this.$refs.passwordField?.$el
      return root?.querySelector?.('input') ?? null
    },
    syncPasswordFromDom() {
      const input = this.getPasswordNativeInput()
      if (!input?.value) return
      if (this.password !== input.value) {
        this.password = input.value
      }
    },
    teardownPasswordAutofillSync() {
      this.pwAutofillTimeouts.forEach(clearTimeout)
      this.pwAutofillTimeouts = []
      const input = this.getPasswordNativeInput()
      if (input && this.pwAutofillOnAnim) {
        input.removeEventListener('animationstart', this.pwAutofillOnAnim)
      }
      this.pwAutofillOnAnim = null
    },
    setupPasswordAutofillSync() {
      this.teardownPasswordAutofillSync()
      const input = this.getPasswordNativeInput()
      if (input) {
        this.pwAutofillOnAnim = (e) => {
          if (e.animationName === 'portal-autofill-start') {
            this.syncPasswordFromDom()
          }
        }
        input.addEventListener('animationstart', this.pwAutofillOnAnim)
      }
      const delays = [0, 50, 100, 200, 400, 800, 1600]
      delays.forEach((ms) => {
        this.pwAutofillTimeouts.push(
          setTimeout(() => this.syncPasswordFromDom(), ms)
        )
      })
    },
    goToRegister() {
      this.$emit('register', {
        tdId: this.documentTipoId,
        nroDoc: this.documentNumber,
      })
    },
    async handleSubmit() {
      const payload = {
        tdId: this.documentTipoId,
        nroDoc: this.documentNumber,
        password: this.password,
      }
      const { valid } = await this.$refs.authForm.validate()
      if (!valid) return
      if (this.documentTipoId == null) {
        this.setAlert({
          type: 'warning',
          message: 'Seleccione el tipo de documento.',
        })
        return
      }
      this.isLoading = true
      try {
        const result = await this.loginWithDocument(payload)
        if (result?.ok) {
          this.resetGuestSession()
          const raw = this.$route.query.redirect
          const redirect = typeof raw === 'string' ? raw.trim() : ''
          const safe =
            redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/'
          await this.$router.replace(safe)
          return
        }
        if (result?.status >= 400 && result?.status < 500) {
          await this.handleLoginRejected(result)
          return
        }
        this.setAlert({
          type: 'error',
          message: 'No se pudo iniciar sesión. Intente nuevamente.',
        })
      } finally {
        this.isLoading = false
      }
    },
    getLoginRejectedMessage(result) {
      const code = String(result?.errorCode ?? '').toLowerCase()
      const backendMessage =
        typeof result?.message === 'string' ? result.message.trim() : ''

      const accountNotActiveCodes = [
        'cuenta_no_activada',
        'cuenta_no_validada',
        'cuenta_pendiente_validacion',
        'email_no_confirmado',
        'email_not_confirmed',
        'usuario_no_confirmado',
        'user_not_confirmed',
      ]
      const notRegisteredCodes = [
        'usuario_no_registrado',
        'paciente_no_registrado',
        'cuenta_no_registrada',
        'portal_usuario_no_registrado',
        'not_registered',
      ]
      const lockedAccountCodes = [
        'cuenta_bloqueada',
        'account_locked',
        'usuario_bloqueado',
        'user_locked',
      ]

      if (code === 'edad_minima_portal_no_cumplida') {
        return 'No cumple con la edad mínima requerida para utilizar el portal.'
      }
      if (lockedAccountCodes.includes(code)) {
        return 'La cuenta se encuentra bloqueada temporalmente. Intente nuevamente más tarde.'
      }
      if (accountNotActiveCodes.includes(code) || result?.status === 403) {
        return 'Debe activar su cuenta para poder ingresar al portal.'
      }
      if (notRegisteredCodes.includes(code) || result?.status === 404) {
        return 'No se encontró una cuenta registrada en el portal con el documento ingresado.'
      }
      if (backendMessage) return backendMessage
      return 'Usuario o contraseña incorrectos.'
    },
    async handleLoginRejected(result) {
      this.password = ''
      this.showPassword = false
      await this.$nextTick()
      await this.$refs.authForm?.validate()
      this.$refs.passwordField?.focus?.()
      this.setAlert({
        type: 'warning',
        message: this.getLoginRejectedMessage(result),
      })
    },
  },
}
</script>

<style scoped>
.auth-form {
  max-width: 28rem;
}
/* En escritorio el margen separa del bloque de branding; en móvil el logo va en el header de color */
.auth-form--login {
  margin-top: 0;
}
@media (min-width: 1280px) {
  .auth-form--login {
    margin-top: 120px;
  }
}
.auth-form__title {
  color: var(--portal-primary);
}
.auth-form__link {
  color: var(--portal-primary);
}
.mobile-help__panel {
  --mobile-help-bg: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-primary), 0.075);
  border-radius: 8px !important;
  color: var(--portal-primary);
  overflow: hidden;
  background: var(--mobile-help-bg) !important;
  box-shadow: none !important;
}
.mobile-help__title {
  min-height: 44px !important;
  padding: 0 14px !important;
  color: var(--portal-primary);
  background: var(--mobile-help-bg);
}
.mobile-help__title :deep(.v-expansion-panel-title__overlay) {
  opacity: 0;
}
.mobile-help__icon {
  color: var(--portal-primary);
}
.mobile-help__text :deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 14px 16px;
  border-top: 1px solid rgba(var(--v-theme-primary), 0.06);
  background: var(--mobile-help-bg);
}
.mobile-help-html {
  color: #000;
  line-height: 1.55;
}
.portal-app--dark .mobile-help-html {
  color: rgba(var(--v-theme-on-surface), 0.78);
}
.mobile-help-html :deep(p) {
  margin-bottom: 0.5rem;
}
.mobile-help-html :deep(p:last-child) {
  margin-bottom: 0;
}
.mobile-help-html :deep(ul) {
  list-style: disc outside;
  padding-left: 1.25rem;
  margin: 0.75rem 0 0;
}
.mobile-help-html :deep(li) {
  margin-bottom: 0.25rem;
}
.mobile-help-html :deep(strong) {
  font-weight: 600;
}
.gap-y-4 > * + * {
  margin-top: 1.25rem;
}
/* Dispara animationstart cuando Chrome autocompleta, para sincronizar v-model y la etiqueta */
:deep(input:-webkit-autofill) {
  animation-name: portal-autofill-start;
  animation-duration: 0.001s;
}
@keyframes portal-autofill-start {
  from {
    opacity: 1;
  }
  to {
    opacity: 1;
  }
}
</style>
